import { useState, useRef, useLayoutEffect } from "react";
import { Campo } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";
import styled from "styled-components";
import { FaEye } from "react-icons/fa";

interface MemoProps {
    campo: Campo;
    modo: string;
    altura?: string;
    largura?: string;
    tudoMaiusculo?: boolean;
    onCampoEntrar?: (e: any) => void;
    getFieldValue: (nome: string) => string;
    handleInputChange: (nome: string, value: string) => void;
    apenasLeitura?: boolean;
    onBlur: (e: any) => void;
    hasError?: boolean;
    maxLength?: number;
    autoGrow?: boolean;
}

const TextAreaContainer = styled.div<{ largura?: string; altura?: string; autoGrow?: boolean }>`
    width: ${(props) => props.largura || "100%"};
    height: ${(props) => (props.autoGrow ? "auto" : props.altura || "auto")};
    position: relative;
`;

const StyledTextArea = styled.textarea<{ hasError?: boolean; autoGrow?: boolean; minAltura?: string }>`
    width: 100%;
    height: ${(props) => (props.autoGrow ? "auto" : "100%")};
    min-height: ${(props) => (props.autoGrow ? props.minAltura || "60px" : "auto")};
    overflow-y: ${(props) => (props.autoGrow ? "hidden" : "auto")};
    resize: none;
    box-sizing: border-box;
    padding: 10px;
    border: 1px solid ${props => props.hasError ? '#dc3545' : '#ced4da'};
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        outline: none;
        border-color: ${props => props.hasError ? '#dc3545' : '#80bdff'};
        box-shadow: 0 0 0 0.2rem rgba(${props => props.hasError ? '220, 53, 69' : '0, 123, 255'}, 0.25);
    }

    &:disabled {
        background-color: #f3f3f3;
        cursor: not-allowed;
    }
`;

// Com limite de caracteres, mostra quanto já foi usado no canto do campo.
const Contador = styled.span<{ $noLimite: boolean }>`
    position: absolute;
    right: 10px;
    bottom: 6px;
    font-size: 11px;
    color: ${props => props.$noLimite ? '#b42318' : '#9ca3af'};
    pointer-events: none;
`;

const EyeIcon = styled(FaEye)`
    position: absolute;
    top: 4px;
    right: 20px;
    cursor: pointer;
    color: #666;
    &:hover {
        color: #000;
    }
`;

const Modal = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-width: 90%;
    max-height: 80%;
    overflow: auto;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
`;

const FormattedText = styled.pre`
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    font-size: 14px;
    margin: 0;
`;

function Memo(props: MemoProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const valorAtual = props.getFieldValue(props.campo.nome);

    const ajustarAltura = () => {
        const el = textAreaRef.current;
        if (!props.autoGrow || !el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };

    useLayoutEffect(() => {
        ajustarAltura();
    }, [valorAtual, props.autoGrow]);

    const handleTextoChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = props.tudoMaiusculo ? event.target.value.toUpperCase() : event.target.value;
        props.handleInputChange(props.campo.nome, value);
        props.campo.onCampoDigitar && props.campo.onCampoDigitar(value);
        ajustarAltura();
    };

    const handleBlur = () => {
        props.campo.onCampoSair && props.campo.onCampoSair(props.getFieldValue(props.campo.nome));
        props.onBlur && props.onBlur(props.campo)
    };

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    return (
        <TextAreaContainer largura={props.largura} altura={props.altura} autoGrow={props.autoGrow}>
            <StyledTextArea
                ref={textAreaRef}
                value={valorAtual}
                onChange={handleTextoChange}
                onBlur={handleBlur}
                disabled={props.apenasLeitura === true || props.modo === TipoFormulario.tpVisualizacao}
                placeholder={props.campo.extraProps?.placeholder ?? "Digite aqui..."}
                required={props.campo.required}
                onFocus={props.onCampoEntrar}
                hasError={props.hasError}
                maxLength={props.maxLength}
                autoGrow={props.autoGrow}
                minAltura={props.altura}
            />
            {props.maxLength && props.modo !== TipoFormulario.tpVisualizacao && (
                <Contador $noLimite={(valorAtual?.length ?? 0) >= props.maxLength}>{valorAtual?.length ?? 0}/{props.maxLength}</Contador>
            )}
            <EyeIcon onClick={toggleModal} />
            {isModalOpen && (
                <>
                    <ModalOverlay onClick={toggleModal} />
                    <Modal>
                        <FormattedText>
                            {props.getFieldValue(props.campo.nome)}
                        </FormattedText>
                    </Modal>
                </>
            )}
        </TextAreaContainer>
    );
}

export default Memo;